import { ReplenishmentRequestStatusSchema } from "@redotech/redo-fulfillment-wms-common/schema/replenishment-request";
import {
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import {
  AdvancedFilterDataSchema,
  FilterGroupSchema,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  OpensearchPaginationCursorSchema,
  SortDirection,
} from "@redotech/redo-views-common/table";
import { VALID_PAGE_SIZES } from "@redotech/redo-views-common/views";
import {
  storageLocationIdFormat,
  transferSessionIdFormat,
} from "@redotech/redo-warehouse-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  TransferLineKeySchema,
  TransferSessionStateSchema,
} from "./transfer-session";

// GET - Dashboard: urgent SKU count
export const getReplenishmentDashboard = {
  name: "getReplenishmentDashboard" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ urgentSkuCount: z.number().int().nonnegative() }),
};

export type GetReplenishmentDashboardInput = z.infer<
  typeof getReplenishmentDashboard.input
>;

// POST - Create replenishment route (transfer session)
const UnsourcedProductSchema = z.object({
  productId: productIdFormat.schema,
  storageLocationId: storageLocationIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
});

export const createReplenishmentRoute = {
  name: "createReplenishmentRoute" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    skuCount: z.number().int().min(1).max(99),
  }),
  output: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    unsourcedProducts: z.array(UnsourcedProductSchema),
  }),
};

export type UnsourcedProduct = z.infer<typeof UnsourcedProductSchema>;

export type CreateReplenishmentRouteInput = z.infer<
  typeof createReplenishmentRoute.input
>;

// POST - Create replenishment route from specific request IDs
export const createReplenishmentRouteFromRequests = {
  name: "createReplenishmentRouteFromRequests" as const,
  input: z.object({ requestIds: z.array(z.string()).min(1) }),
  output: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    unsourcedProducts: z.array(UnsourcedProductSchema),
    locationId: locationIdFormat.schema,
  }),
};

export type CreateReplenishmentRouteFromRequestsInput = z.infer<
  typeof createReplenishmentRouteFromRequests.input
>;

// POST - Dismiss replenishment request(s) for a specific bin+product
export const dismissReplenishmentRequest = {
  name: "dismissReplenishmentRequest" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: productIdFormat.schema,
    note: z.string().optional(),
  }),
  output: z.object({
    dismissedCount: z.number().int().nonnegative(),
    transferState: TransferSessionStateSchema,
  }),
};

export type DismissReplenishmentRequestInput = z.infer<
  typeof dismissReplenishmentRequest.input
>;

// POST - Fail replenishment request(s) for a specific source bin+product (no backstock found)
export const failReplenishmentRequest = {
  name: "failReplenishmentRequest" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: productIdFormat.schema,
    note: z.string().optional(),
  }),
  output: z.object({
    failedCount: z.number().int().nonnegative(),
    transferState: TransferSessionStateSchema,
  }),
};

export type FailReplenishmentRequestInput = z.infer<
  typeof failReplenishmentRequest.input
>;

// POST - Release a replenishment session for pickup by another user
export const releaseReplenishmentSession = {
  name: "releaseReplenishmentSession" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ success: z.boolean() }),
};

export type ReleaseReplenishmentSessionInput = z.infer<
  typeof releaseReplenishmentSession.input
>;

export const reduceReplenishmentPick = {
  name: "reduceReplenishmentPick" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    productId: TransferLineKeySchema,
    quantity: z.number().int().positive(),
  }),
  output: z.object({ transferState: TransferSessionStateSchema }),
};

export const removeReplenishmentProduct = {
  name: "removeReplenishmentProduct" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    productId: productIdFormat.schema,
    note: z.string().optional(),
  }),
  output: z.object({
    sessionCancelled: z.boolean(),
    transferState: TransferSessionStateSchema.nullable(),
  }),
};

const SourcedReplenishmentSessionSchema = z.object({
  transferSessionId: transferSessionIdFormat.schema,
  products: z.array(
    z.object({
      productId: productIdFormat.schema,
      sku: z.string().nullable(),
      title: z.string(),
      imageUrl: z.string().nullable(),
      sourceBinName: z.string().nullable(),
      destinationBinName: z.string().nullable(),
      lotNumber: z.string().nullable(),
      expirationDate: z.string().nullable(),
    }),
  ),
});

export const listSourcedReplenishments = {
  name: "listSourcedReplenishments" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ sessions: z.array(SourcedReplenishmentSessionSchema) }),
};

export type ListSourcedReplenishmentsInput = z.infer<
  typeof listSourcedReplenishments.input
>;

// GET - List in-progress sourcing replenishment sessions
export const listInProgressSourcingSessions = {
  name: "listInProgressSourcingSessions" as const,
  input: z.object({ locationId: locationIdFormat.schema }),
  output: z.object({ sessions: z.array(SourcedReplenishmentSessionSchema) }),
};

export type ListInProgressSourcingSessionsInput = z.infer<
  typeof listInProgressSourcingSessions.input
>;

// POST - Claim a sourced replenishment session for distribution
export const claimSourcedReplenishment = {
  name: "claimSourcedReplenishment" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ success: z.boolean() }),
};

export type ClaimSourcedReplenishmentInput = z.infer<
  typeof claimSourcedReplenishment.input
>;

// POST - Specify source bin for an unsourced replenishment product
export const specifyReplenishmentSource = {
  name: "specifyReplenishmentSource" as const,
  input: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    productId: productIdFormat.schema,
    destinationStorageLocationId: storageLocationIdFormat.schema.optional(),
    sourceBinName: z.string().min(1),
    replaceSourceStorageLocationId: storageLocationIdFormat.schema.optional(),
    quantity: z.number().int().positive().optional(),
  }),
  output: z.object({
    success: z.boolean(),
    transferState: TransferSessionStateSchema,
  }),
};

export type SpecifyReplenishmentSourceInput = z.infer<
  typeof specifyReplenishmentSource.input
>;

// GET - List unsourced products for a replenishment session
export const getUnsourcedReplenishmentProducts = {
  name: "getUnsourcedReplenishmentProducts" as const,
  input: z.object({ transferSessionId: transferSessionIdFormat.schema }),
  output: z.object({ unsourcedProducts: z.array(UnsourcedProductSchema) }),
};

export type GetUnsourcedReplenishmentProductsInput = z.infer<
  typeof getUnsourcedReplenishmentProducts.input
>;

const ReplenishmentRequestLotSchema = z.object({
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  lotNumber: z.string().nullable(),
  expirationDate: z.string().nullable(),
  quantity: z.number().nonnegative(),
});

export type ReplenishmentRequestLot = z.infer<
  typeof ReplenishmentRequestLotSchema
>;

// GET - List open replenishment requests for the table view
const ReplenishmentRequestRowSchema = z.object({
  id: z.string(),
  storageLocationId: storageLocationIdFormat.schema,
  binName: z.string(),
  productId: productIdFormat.schema,
  productTitle: z.string().nullable(),
  sku: z.string().nullable(),
  status: ReplenishmentRequestStatusSchema,
  sourceStorageLocationId: storageLocationIdFormat.schema.nullable(),
  sourceBinName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
  unpickedShipmentCount: z.number().int().nonnegative(),
  unpickedItemTotal: z.number().nonnegative(),
  destinationLots: z.array(ReplenishmentRequestLotSchema),
});

export const listReplenishmentRequests = {
  name: "listReplenishmentRequests" as const,
  input: z.object({
    pageNumber: z.number().int().nonnegative(),
    pageSize: z.number().int().min(1),
    search: z.string().optional(),
  }),
  output: z.object({
    requests: z.array(ReplenishmentRequestRowSchema),
    total: z.number().int().nonnegative(),
  }),
};

export type ReplenishmentRequestTableRow = z.infer<
  typeof ReplenishmentRequestRowSchema
>;

export type ListReplenishmentRequestsInput = z.infer<
  typeof listReplenishmentRequests.input
>;

// GET - Search open replenishment requests (OpenSearch-backed). Returns rows
// plus an opensearch pagination cursor; total count comes from
// getReplenishmentRequestsCount.
export const searchReplenishmentRequests = {
  name: "searchReplenishmentRequests" as const,
  input: z.object({
    pageNumber: z.number().int().nonnegative(),
    pageSize: z
      .number()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .refine((value) => VALID_PAGE_SIZES.includes(value as any), {
        message: `pageSize must be one of: ${VALID_PAGE_SIZES.join(", ")}`,
      }),
    search: z.string().nullish(),
    sort: z
      .object({ key: z.string(), direction: createEnum(SortDirection) })
      .nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    requests: z.array(ReplenishmentRequestRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export type SearchReplenishmentRequestsInput = z.infer<
  typeof searchReplenishmentRequests.input
>;

// GET - Count of open replenishment requests matching the given filters.
export const getReplenishmentRequestsCount = {
  name: "getReplenishmentRequestsCount" as const,
  input: z.object({
    search: z.string().nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
  }),
  output: z.object({ total: z.number().int().nonnegative() }),
};

export type GetReplenishmentRequestsCountInput = z.infer<
  typeof getReplenishmentRequestsCount.input
>;

const ReplenishmentReportBinSchema = z.object({
  id: z.string(),
  name: z.string(),
  quantity: z.number(),
});

const ReplenishmentReportRowSchema = z.object({
  productId: productIdFormat.schema,
  title: z.string().nullable(),
  imageUrl: z.string().nullable(),
  sku: z.string().nullable(),
  upc: z.string().nullable(),
  pickableOnHand: z.number(),
  overstockOnHand: z.number(),
  receivingOnHand: z.number(),
  pickableBinCount: z.number(),
  pickableBins: z.array(ReplenishmentReportBinSchema),
  emptyPickableBins: z.array(ReplenishmentReportBinSchema),
  overstockBins: z.array(ReplenishmentReportBinSchema),
  pickableCapacity: z.number().nullable(),
  hasEmptyPickableBin: z.boolean(),
  demandShortfall: z.number(),
  shortOfStock: z.boolean(),
  unpickedShipmentCount: z.number(),
  unpickedItemTotal: z.number(),
  pickableShortfall: z.number(),
  minimumPickable: z.number(),
  maximumPickable: z.number().nullable(),
  increment: z.number().nullable(),
  hasThreshold: z.boolean(),
  belowMinimum: z.boolean(),
  fillRatio: z.number(),
  blockedReason: z.string().nullable(),
  lotTracked: z.boolean(),
  sameLotAvailable: z.boolean(),
  hasPendingReplenishment: z.boolean(),
  openReplenishmentStatus: z.string().nullable(),
  openReplenishmentStartedAt: z.string().nullable(),
  needsReplenishment: z.boolean(),
  suggestedQuantity: z.number(),
});

export type ReplenishmentReportTableRow = z.infer<
  typeof ReplenishmentReportRowSchema
>;

// GET - Products at a warehouse with their pickable, backstock, and demand
// figures, read from the location bin inventory index.
export const searchReplenishmentReport = {
  name: "searchReplenishmentReport" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    pageNumber: z.number().int().nonnegative(),
    pageSize: z
      .number()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      .refine((value) => VALID_PAGE_SIZES.includes(value as any), {
        message: `pageSize must be one of: ${VALID_PAGE_SIZES.join(", ")}`,
      }),
    search: z.string().nullish(),
    sort: z
      .object({ key: z.string(), direction: createEnum(SortDirection) })
      .nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    rows: z.array(ReplenishmentReportRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export type SearchReplenishmentReportInput = z.infer<
  typeof searchReplenishmentReport.input
>;

// GET - Count of report rows matching the given filters.
export const getReplenishmentReportCount = {
  name: "getReplenishmentReportCount" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    search: z.string().nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
  }),
  output: z.object({ total: z.number().int().nonnegative() }),
};

export const ReplenishmentReportActionSchema = createEnum(["start", "assign"], {
  enumName: "ReplenishmentReportAction",
});

// POST - Create replenishment requests for products chosen on the report, each
// with a suggested destination bin, then either start a route for the caller
// or hand the resulting tasks to another user.
export const startReplenishmentFromReport = {
  name: "startReplenishmentFromReport" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productIds: z.array(productIdFormat.schema).min(1),
    action: ReplenishmentReportActionSchema,
    assignedUserId: z
      .string()
      .regex(/^[0-9a-f]{24}$/i)
      .nullish(),
    assignedRole: z.string().nullish(),
  }),
  output: z.object({
    requestIds: z.array(z.string()),
    skippedProductIds: z.array(productIdFormat.schema),
    inProgressProductIds: z.array(productIdFormat.schema),
    noBackstockProductIds: z.array(productIdFormat.schema),
    transferSessionId: transferSessionIdFormat.schema.nullable(),
    unsourcedProducts: z.array(UnsourcedProductSchema),
    assignedTaskCount: z.number().int().nonnegative(),
  }),
};

export type StartReplenishmentFromReportInput = z.infer<
  typeof startReplenishmentFromReport.input
>;

// POST - Cancel every pending or in-progress request for the given products at
// a warehouse, unwinding any transfer session that backs them.
export const cancelReplenishmentFromReport = {
  name: "cancelReplenishmentFromReport" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productIds: z.array(productIdFormat.schema).min(1),
  }),
  output: z.object({ cancelledCount: z.number().int().nonnegative() }),
};

export const ReplenishmentResumePhaseSchema = createEnum([
  "source",
  "distribute",
]);

export const resumeReplenishmentFromReport = {
  name: "resumeReplenishmentFromReport" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productId: productIdFormat.schema,
  }),
  output: z.object({
    transferSessionId: transferSessionIdFormat.schema,
    phase: ReplenishmentResumePhaseSchema,
  }),
};

// GET - Distinct SKU values across the caller's open replenishment requests.
// Used to populate the SKU advanced-filter dropdown.
export const getReplenishmentRequestDistinctSkus = {
  name: "getReplenishmentRequestDistinctSkus" as const,
  input: z.object({}),
  output: z.object({ skus: z.array(z.string()) }),
};

// POST - Manually create a single replenishment request. Returns the new
// request id, or throws if a pending/in-progress request already exists for
// the same (bin, product) pair.
export const createReplenishmentRequest = {
  name: "createReplenishmentRequest" as const,
  input: z.object({
    locationId: locationIdFormat.schema,
    productId: productIdFormat.schema,
    storageLocationId: storageLocationIdFormat.schema,
    sourceStorageLocationId: storageLocationIdFormat.schema.nullish(),
    note: z.string().nullish(),
  }),
  output: z.object({ id: z.string() }),
};

export type CreateReplenishmentRequestInput = z.infer<
  typeof createReplenishmentRequest.input
>;

// GET - Distinct destination-bin names across the caller's open replenishment
// requests. Used to populate the bin advanced-filter dropdown.
export const getReplenishmentRequestDistinctBinNames = {
  name: "getReplenishmentRequestDistinctBinNames" as const,
  input: z.object({}),
  output: z.object({ binNames: z.array(z.string()) }),
};

// POST - Cancel multiple pending replenishment requests by ID
export const cancelReplenishmentRequests = {
  name: "cancelReplenishmentRequests" as const,
  input: z.object({ requestIds: z.array(z.string()).min(1) }),
  output: z.object({ cancelledCount: z.number() }),
};

export type CancelReplenishmentRequestsInput = z.infer<
  typeof cancelReplenishmentRequests.input
>;
